import * as Dialog from "@radix-ui/react-dialog";
import { useState, useRef } from "react";
import PencomDropdown from "@/components/input/PencomDropdown";
import PencomTextField from "@/components/input/PencomTextField";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { useFileUpload } from "@/hooks/file-upload/file-upload.hook";
import { EStorage } from "@/constants/enums/storage.enum";
import { env } from "@/utils/env";
import { ENDPOINTS } from "@/constants/endpoints";

const REMOVAL_REASONS = [
    { id: "resignation", label: "Employee resignation" },
    { id: "retirement", label: "Employee retirement" },
    { id: "termination", label: "Employee termination" },
    { id: "death", label: "Employee death" },
    { id: "transfer", label: "Inter-company transfer" },
    { id: "other", label: "Other" },
];

const REASONS_REQUIRING_DOCUMENTS = new Set(["resignation", "termination", "death", "transfer","retirement"]);

interface RemoveEmployeeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    employeeName: string;
    isLoading: boolean;
    onConfirm: (reason: string, documentUrls: string[], comment: string) => void;
}

export const RemoveEmployeeDialog = ({
    open,
    onOpenChange,
    employeeName,
    isLoading,
    onConfirm,
}: RemoveEmployeeDialogProps) => {
    const [selectedReason, setSelectedReason] = useState("");
    const [otherReason, setOtherReason] = useState("");
    const [comment, setComment] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
    const fileUrlMapRef = useRef<Map<string, string>>(new Map());

    const { mutate: uploadFile, isPending: isUploading } = useFileUpload();

    const isOther = selectedReason === "other";
    const requiresDocs = REASONS_REQUIRING_DOCUMENTS.has(selectedReason);
    const resolvedReason = isOther ? otherReason.trim() : selectedReason;
    const canSubmit = resolvedReason.length > 0 && comment.trim().length > 0 && !isLoading && !isUploading && (!requiresDocs || uploadedUrls.length > 0);

    const handleFilesSelect = (files: File[]) => {
        const newFiles = files.filter((file) => {
            const key = `${file.name}-${file.size}-${file.lastModified}`;
            return !fileUrlMapRef.current.has(key);
        });

        setSelectedFiles(files);

        for (const file of newFiles) {
            const key = `${file.name}-${file.size}-${file.lastModified}`;
            uploadFile(
                { file, path: EStorage.EMPLOYEE_REMOVAL, document_type: EStorage.EMPLOYEE_REMOVAL },
                {
                    onSuccess: (response) => {
                        const raw: string =
                            (response as { url?: string })?.url ??
                            (response as { data?: { url?: string } })?.data?.url ??
                            (Array.isArray(response) ? (response[0] as string) : "");
                        if (!raw) return;
                        const url = raw.startsWith("http")
                            ? raw
                            : `${env.API_BASE_URL}${ENDPOINTS.DOWNLOAD_CERTIFICATE(raw)}`;
                        fileUrlMapRef.current.set(key, url);
                        setUploadedUrls(Array.from(fileUrlMapRef.current.values()));
                    },
                }
            );
        }
    };

    const handleFileRemoveAt = (index: number) => {
        const removedFile = selectedFiles[index];
        if (removedFile) {
            const key = `${removedFile.name}-${removedFile.size}-${removedFile.lastModified}`;
            fileUrlMapRef.current.delete(key);
            setUploadedUrls(Array.from(fileUrlMapRef.current.values()));
        }
    };

    const handleConfirm = () => {
        if (!canSubmit) return;
        const label = isOther
            ? otherReason.trim()
            : REMOVAL_REASONS.find((r) => r.id === selectedReason)?.label ?? selectedReason;
        onConfirm(label, uploadedUrls, comment.trim());
    };

    const resetState = () => {
        setSelectedReason("");
        setOtherReason("");
        setComment("");
        setSelectedFiles([]);
        setUploadedUrls([]);
        fileUrlMapRef.current.clear();
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!isLoading) {
            if (!nextOpen) resetState();
            onOpenChange(nextOpen);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50" />
                <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-[480px] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus:outline-none">
                    <div className="flex items-start justify-between p-6 pb-4 border-b border-stroke">
                        <div className="flex flex-col gap-y-1 pr-8">
                            <Dialog.Title className="font-clash-display font-semibold text-lg text-black-dark-grey leading-normal">
                                Remove employee
                            </Dialog.Title>
                            <Dialog.Description className="font-clash-display font-regular text-sm text-medium-dark-grey">
                                Are you sure you want to remove{" "}
                                <span className="font-semibold">{employeeName}</span>?
                            </Dialog.Description>
                        </div>
                        <Dialog.Close
                            disabled={isLoading}
                            className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none transition-opacity cursor-pointer shrink-0 mt-0.5 disabled:cursor-not-allowed"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Dialog.Close>
                    </div>

                    <div className="flex flex-col gap-y-4 p-6">
                        <PencomDropdown
                            id="removal-reason"
                            label="Reason for removal"
                            placeholder="Select a reason"
                            options={REMOVAL_REASONS}
                            value={selectedReason}
                            onChange={(e) => {
                                setSelectedReason(e.target.value);
                                setOtherReason("");
                            }}
                        />

                        {isOther && (
                            <PencomTextField
                                id="removal-reason-other"
                                label="Please specify"
                                type="text"
                                placeholder="Enter reason for removal"
                                value={otherReason}
                                multiline
                                rows={3}
                                onTextareaChange={(e) => setOtherReason(e.target.value)}
                            />
                        )}

                        {requiresDocs && (
                            <PencomFileUpload
                                id="removal-supporting-documents"
                                label="Supporting document(s)"
                                maxFiles={5}
                                selectedFiles={selectedFiles}
                                onFilesSelect={handleFilesSelect}
                                onFileRemoveAt={handleFileRemoveAt}
                                isUploading={isUploading}
                                hint="Upload the relevant supporting document(s) for this removal"
                            />
                        )}

                        <PencomTextField
                            id="removal-comment"
                            label="Additional comments"
                            type="text"
                            placeholder="Provide any additional context for this removal..."
                            value={comment}
                            multiline
                            rows={3}
                            onTextareaChange={(e) => setComment(e.target.value)}
                        />

                        <div className="flex flex-col gap-y-3 mt-2">
                            <PrimaryButton
                                title="Yes, Remove Employee"
                                onClick={handleConfirm}
                                disabled={!canSubmit}
                                isLoading={isLoading}
                                className="w-full bg-dark-red border-dark-red hover:bg-dark-red"
                            />
                            <button
                                onClick={() => handleOpenChange(false)}
                                disabled={isLoading}
                                className="w-full h-[50px] font-clash-display text-md font-semibold text-medium-dark-grey border border-stroke rounded-lg hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                No, Keep Employee
                            </button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
